import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables!');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Omzeil het interne lock-mechanisme dat bij paginaherlaad vastloopt
    // doordat token-refresh de lock te lang vasthoudt.
    lock: async (name, acquireTimeout, fn) => fn()
  }
});

// Pagina rechten constanten
export const ALL_MAIN_PAGES = [
  { key: 'dashboard', label: 'Overzicht' },
  { key: 'kenteken_scans', label: 'Kenteken Scans' },
  { key: 'vehicles', label: 'Kentekens' },
  { key: 'people_counting', label: 'Personen Telling' },
  { key: 'mijnetickets', label: 'MijnEtickets' },
];

export const DEFAULT_ALLOWED_PAGES = ALL_MAIN_PAGES.map(p => p.key);

// Helper: controleer of een gebruiker toegang heeft tot een pagina
export function hasPageAccess(userProfile, pageKey) {
  if (!userProfile) return false;
  if (userProfile.role === 'admin') return true;
  const pages = userProfile.allowed_pages || DEFAULT_ALLOWED_PAGES;
  return pages.includes(pageKey);
}

// Auth Service
export const authService = {
  async getEmailFromUsername(username) {
    // Use the server-side function to get email from username
    // This works even for non-authenticated users (needed for login)
    const { data, error } = await supabase.rpc('get_email_from_username', {
      p_username: username
    });

    if (error) throw new Error('Gebruikersnaam niet gevonden');
    if (!data) throw new Error('Geen email gekoppeld aan deze gebruikersnaam');

    return data;
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;

    if (data.user) {
      await supabase
        .from('user_profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.user.id);
    }

    return data;
  },

  async signInWithAzure() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile',
        redirectTo: window.location.origin
      }
    });
    if (error) throw error;
    return data;
  },

  async signInWithUsername(username, password) {
    // Controleer of account geblokkeerd is
    const { data: lockout } = await supabase.rpc('check_login_lockout', { p_username: username });
    if (lockout?.locked) {
      throw new Error(`Account geblokkeerd. Probeer opnieuw over ${lockout.remaining_minutes} minuten.`);
    }

    const email = await this.getEmailFromUsername(username);

    // Controleer of account actief is
    const { data: profileCheck } = await supabase
      .from('user_profiles')
      .select('active')
      .eq('username', username)
      .maybeSingle();
    if (profileCheck && profileCheck.active === false) {
      throw new Error('Dit account is gedeactiveerd. Neem contact op met een beheerder.');
    }

    try {
      const result = await this.signIn(email, password);
      // Reset pogingen na geslaagde login
      await supabase.rpc('reset_login_attempts', { p_username: username });
      return result;
    } catch (error) {
      // Registreer mislukte poging
      const { data: failResult } = await supabase.rpc('record_failed_login', { p_username: username });
      if (failResult?.locked) {
        throw new Error('Te veel mislukte pogingen. Account geblokkeerd voor 30 minuten.');
      }
      const remaining = 3 - (failResult?.attempts || 0);
      if (remaining > 0) {
        throw new Error(`${error.message} (${remaining} poging${remaining !== 1 ? 'en' : ''} resterend)`);
      }
      throw error;
    }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    // Ignore "Auth session missing" errors as the user is already logged out
    if (error && error.message !== 'Auth session missing!') {
      throw error;
    }
  },

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async getMfaLevel() {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw error;
    return data;
  },

  async getMfaFactors() {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return data;
  },

  async enrollMfa() {
    // Verwijder onverified factors van eerdere, afgebroken enrollments (best-effort)
    // Supabase plaatst onverified factors alleen in 'all', niet in 'totp'
    const { data: existing } = await supabase.auth.mfa.listFactors();
    const unverified = existing?.all?.filter(f => f.factor_type === 'totp' && f.status !== 'verified') || [];
    for (const f of unverified) {
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }

    // Gebruik unieke naam om naamconflicten te voorkomen als unenroll mislukt
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      issuer: 'ZF-Portaal',
      friendlyName: `ZF-Portaal Authenticator ${Date.now()}`
    });
    if (error) throw error;
    return data;
  },

  async challengeMfa(factorId) {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId });
    if (error) throw error;
    return data;
  },

  async verifyMfa(factorId, challengeId, code) {
    const { data, error } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
    if (error) throw error;
    return data;
  },

  async unenrollMfa(factorId) {
    const { data, error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) throw error;
    return data;
  }
};

// Vehicle Service
export const vehicleService = {
  async getAll() {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .order('kenteken');
    
    if (error) throw error;
    return data;
  },

  async getByKenteken(kenteken) {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('kenteken', kenteken)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(vehicleData) {
    const { data, error } = await supabase
      .from('vehicles')
      .insert([vehicleData])
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async update(kenteken, updates) {
    const { data, error } = await supabase
      .from('vehicles')
      .update(updates)
      .eq('kenteken', kenteken)
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async delete(kenteken) {
    const { error } = await supabase
      .from('vehicles')
      .delete()
      .eq('kenteken', kenteken);
    
    if (error) throw error;
  }
};

// Scan Service
export const scanService = {
  async getRecent(limit = 20) {
    const { data, error } = await supabase
      .from('scans')
      .select('*')
      .order('scan_timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  async insert(kenteken, status, scannedBy) {
    const { data, error } = await supabase
      .from('scans')
      .insert([{
        kenteken,
        status,
        scanned_by: scannedBy || null,
        scan_timestamp: new Date().toISOString()
      }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async lookupVehicle(kenteken) {
    const { data, error } = await supabase
      .from('vehicles')
      .select('*')
      .eq('kenteken', kenteken)
      .maybeSingle();
    if (error) throw error;
    return data; // null als niet gevonden
  }
};

// Stats Service
export const statsService = {
  async getDashboardStats() {
    const { data, error } = await supabase.rpc('get_dashboard_stats');

    if (error) throw error;
    return data;
  }
};

// User Service
export const userService = {
  async getUserProfile(userId) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  },

  async getAllUsers() {
    // Gebruik de veilige RPC functie (admin-only, geen exposed auth.users view)
    const { data, error } = await supabase.rpc('get_all_users_with_email');

    if (error) {
      console.warn('get_all_users_with_email RPC fout:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });

      // Fallback naar user_profiles zonder email als functie niet werkt
      const { data: profiles, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profileError) throw profileError;
      return profiles;
    }

    return data;
  },

  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select();

    if (error) throw error;
    return data[0];
  },

  async updateUserPassword(userId, newPassword) {
    // Use server-side function to update password
    // This requires admin privileges which are checked in the function
    const { data, error } = await supabase.rpc('update_user_password', {
      p_user_id: userId,
      p_new_password: newPassword
    });

    if (error) throw error;

    // Check if the function returned an error
    if (data && !data.success) {
      throw new Error(data.error || 'Fout bij wijzigen wachtwoord');
    }

    return data;
  },

  async createUser(email, password, role = 'gebruiker', username = null, allowedPages = null) {
    // Generate username from email if not provided
    const generatedUsername = username || email.split('@')[0];

    try {
      // First, create the auth user using Supabase Auth signup
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: generatedUsername,
            role: role
          }
        }
      });

      if (signUpError) throw signUpError;

      // Then create the user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .insert([{
          id: signUpData.user.id,
          username: generatedUsername,
          role: role,
          active: true,
          allowed_pages: allowedPages || DEFAULT_ALLOWED_PAGES
        }])
        .select()
        .single();

      if (profileError) throw profileError;

      return profileData;
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error(`Kan gebruiker niet aanmaken: ${error.message}`);
    }
  },

  async unlockUser(userId) {
    const { error } = await supabase
      .from('user_profiles')
      .update({ failed_login_attempts: 0, locked_until: null })
      .eq('id', userId);
    if (error) throw error;
  },

  async deleteUser(userId) {
    // Use server-side function to delete user
    // This requires admin privileges which are checked in the function
    const { data, error } = await supabase.rpc('delete_user_account', {
      p_user_id: userId
    });

    if (error) throw error;

    // Check if the function returned an error
    if (data && !data.success) {
      throw new Error(data.error || 'Fout bij verwijderen gebruiker');
    }

    return data;
  }
};

// ==============================================================================
// People Counting Service
// ==============================================================================

// Helper function to get the start of the "event day"
// For events that run past midnight, the day starts at 5:00 AM
const DAY_START_HOUR = 5;

function getEventDayStart() {
  const now = new Date();
  const eventDayStart = new Date(now);

  // If current time is before 5:00 AM, the event day started yesterday at 5:00 AM
  if (now.getHours() < DAY_START_HOUR) {
    eventDayStart.setDate(eventDayStart.getDate() - 1);
  }

  eventDayStart.setHours(DAY_START_HOUR, 0, 0, 0);
  return eventDayStart;
}

export const peopleCountingService = {
  // Get all settings
  async getSettings() {
    const { data, error } = await supabase
      .from('people_counting_settings')
      .select('*')
      .order('setting_key');

    if (error) throw error;
    return data || [];
  },

  // Update a single setting
  async updateSetting(key, value, isEncrypted = false) {
    // Note: Encryption happens on the backend
    // For now, we send plain values and let the backend handle encryption
    const { data, error } = await supabase
      .from('people_counting_settings')
      .upsert({
        setting_key: key,
        setting_value: value,
        is_encrypted: isEncrypted
      }, {
        onConflict: 'setting_key'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Test camera connection - controleert de laatste sync log van de Raspberry Pi
  async testConnection() {
    const { data, error } = await supabase
      .from('people_counting_sync_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return {
        success: false,
        error: 'Geen sync data gevonden. Is de Raspberry Pi camera sync actief?'
      };
    }

    const lastSync = new Date(data.created_at);
    const minutesAgo = Math.round((Date.now() - lastSync.getTime()) / 60000);
    const isSuccess = data.status === 'success';

    return {
      success: isSuccess,
      message: isSuccess
        ? `Laatste sync: ${minutesAgo} minuten geleden (${data.records_synced || 0} records)`
        : (data.error_message || data.message || 'Onbekende fout'),
      error: isSuccess ? null : (data.error_message || data.message),
      data: {
        deviceType: 'Dahua Camera (via Raspberry Pi)',
        lastSync: lastSync.toLocaleString('nl-NL'),
        minutesAgo,
      }
    };
  },

  // Get dashboard statistics (event day starts at 5:00 AM)
  async getDashboardStats() {
    const eventDayStart = getEventDayStart();

    const { data, error } = await supabase
      .rpc('get_people_counting_dashboard_stats', {
        p_day_start: eventDayStart.toISOString()
      });

    if (error) throw error;
    return data || {
      current_hour: { entered: 0, exited: 0, net: 0, inside: 0, last_update: null },
      today: { entered: 0, exited: 0, net: 0, peak_inside: 0 },
      total_records: 0,
      last_sync: null
    };
  },

  // Get today's hourly data (event day starts at 5:00 AM)
  async getTodayHourly() {
    const eventDayStart = getEventDayStart();

    const { data, error } = await supabase
      .from('people_counts')
      .select('*')
      .gte('start_time', eventDayStart.toISOString())
      .eq('granularity', 'Hour')
      .order('start_time', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Get historical data
  async getHistorical(from, to, granularity = 'Hour') {
    const { data, error } = await supabase
      .from('people_counts')
      .select('*')
      .gte('start_time', from)
      .lte('end_time', to)
      .eq('granularity', granularity)
      .order('start_time', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Get current hour stats
  async getCurrentHourStats() {
    const { data, error } = await supabase
      .rpc('get_current_hour_stats', { p_channel: 1 });

    if (error) throw error;
    return data || [];
  },

  // Get today's stats (event day starts at 5:00 AM)
  async getTodayStats() {
    const eventDayStart = getEventDayStart();

    const { data, error } = await supabase
      .rpc('get_today_stats', {
        p_channel: 1,
        p_day_start: eventDayStart.toISOString()
      });

    if (error) throw error;
    return data || [];
  },

  // Get sync logs
  async getSyncLogs(limit = 10) {
    const { data, error } = await supabase
      .from('people_counting_sync_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }
};

// ==============================================================================
// Security Service
// ==============================================================================

export const securityService = {
  async logLoginPoging({ gebruikersnaam, geslaagd, foutmelding, actie = 'inloggen' }) {
    let ipAdres = null;
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      ipAdres = data.ip;
    } catch {}

    const { error } = await supabase
      .from('security_log')
      .insert([{
        actie,
        gebruikersnaam: gebruikersnaam || null,
        ip_adres: ipAdres,
        browser: navigator.userAgent,
        geslaagd,
        foutmelding: foutmelding || null
      }]);

    if (error) console.error('Security log fout:', error);
  },

  async getLogs(limit = 100) {
    const { data, error } = await supabase
      .from('security_log')
      .select('*')
      .order('tijdstip', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }
};

// ==============================================================================
// Audit Service
// ==============================================================================

export const scannerTokenService = {
  async getAll() {
    const { data, error } = await supabase
      .from('scanner_tokens')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async create(naam) {
    const { data, error } = await supabase
      .from('scanner_tokens')
      .insert([{ naam }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async revoke(id) {
    const { error } = await supabase
      .from('scanner_tokens')
      .update({ active: false })
      .eq('id', id);
    if (error) throw error;
  }
};

export const auditService = {
  async log({ changedBy, action, tableName, recordId, oldData, newData }) {
    const { error } = await supabase
      .from('audit_log')
      .insert([{
        changed_by: changedBy || null,
        action,
        table_name: tableName,
        record_id: recordId ? String(recordId) : null,
        old_data: oldData || null,
        new_data: newData || null
      }]);

    if (error) console.error('Audit log fout:', error);
  }
};

// ==============================================================================
// App Settings Service
// ==============================================================================

export const appSettingsService = {
  async getSetting(key) {
    const { data, error } = await supabase
      .from('people_counting_settings')
      .select('setting_value')
      .eq('setting_key', key)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data?.setting_value || null;
  },

  async setSetting(key, value) {
    const { data, error } = await supabase
      .from('people_counting_settings')
      .upsert({
        setting_key: key,
        setting_value: String(value),
        is_encrypted: false
      }, {
        onConflict: 'setting_key'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

// ==============================================================================
// MijnETickets Service
// ==============================================================================
// API calls verlopen via een Supabase Edge Function (mijnetickets-proxy).
// De API key staat hierdoor alleen server-side en is niet zichtbaar in de browser.

// Request cache om 429 rate limit fouten te voorkomen
const apiCache = new Map();
const CACHE_TTL = 30000; // 30 seconden

export const mijneTicketsService = {
  // Roep de Edge Function aan als proxy voor de MijnETickets API
  async fetchApi(path) {
    // Check cache
    const cached = apiCache.get(path);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    const { data, error } = await supabase.functions.invoke('mijnetickets-proxy', {
      body: { path },
    });

    if (error) {
      throw new Error(`Proxy fout: ${error.message}`);
    }
    if (data?.error) {
      throw new Error(`API fout: ${data.error}`);
    }

    apiCache.set(path, { data, timestamp: Date.now() });
    return data;
  },

  // Get event details
  async getEvent() {
    return this.fetchApi('/events/{eventId}');
  },

  // Get all tickets for the event
  async getTickets() {
    return this.fetchApi('/events/{eventId}/tickets');
  },

  // Get sales statistics for a period
  async getSalesStats(from, till, period = 'hour') {
    const params = new URLSearchParams({ from, till, period });
    return this.fetchApi(`/events/{eventId}/stats/sales?${params}`);
  },

  // Get scan statistics for a period (interval in minutes: 5, 10, or 15)
  async getScanStats(from, till, interval = '10') {
    const params = new URLSearchParams({ from, till, interval });
    return this.fetchApi(`/events/{eventId}/stats/scans?${params}`);
  },

  // Get orders for the event
  async getOrders(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.fetchApi(`/events/{eventId}/orders?${params}`);
  },

  // Get ticket types for the event
  async getTicketTypes() {
    return this.fetchApi('/events/{eventId}/tickettypes');
  },

  // Altijd geconfigureerd — key en event ID staan als Supabase secrets
  isConfigured() {
    return true;
  }
};